/**
 * Typed custom events for cross-component communication.
 *
 * Centralizes all window-level custom events so implicit dependencies
 * become visible and type-checked at compile time.
 *
 * @example
 * // Dispatch
 * dispatchAppEvent('route-change', '/home')
 *
 * // Listen (returns cleanup function for useEffect)
 * useEffect(() => onAppEvent('tab-drag-move', () => remeasure()), [])
 */

import type { LifeSpace, Task } from './db/models'
import type { DiscoverStepId } from './intro/steps'

interface AppEventMap {
  /** Fired by RouteTracker when React Router location changes. Payload: pathname */
  'route-change': string
  /** Fired by TabBar during active tab drag. No payload. */
  'tab-drag-move': undefined
  /** Fired by TabBar when drag ends. No payload. */
  'tab-drag-end': undefined
  /** Suppress scroll-spy during programmatic scrolls (CMD/Alt+Arrow, sidebar click). */
  'scroll-spy-suppress': undefined
  /** Set the active section in the sidebar after keyboard navigation. Payload: sectionId or null. */
  'scroll-spy-set-section': string | null
  /** Scroll to a section header (sidebar sub-menu click or initial load). Payload: sectionId. */
  'scroll-to-section': string
  /** Fired after the current user's profile (avatar, name, …) was updated. Listeners refetch. */
  'user-profile-updated': undefined
  /** Sidebar "Kurz notiert" was clicked — leave Einsortieren (Clarify) mode. Payload: lifeSpace. */
  'quick-notes-exit-clarify': string
  /**
   * Pfeil an einer Unteraufgabe in der aufgeklappten Karte (T14 #179): Die Seite dieses
   * Tabs wählt die Aufgabe, öffnet die Aufgaben-Details und zeigt darin die Unteraufgabe.
   */
  'open-subtask': { tabId: string; parentId: string; childId: string }
  /**
   * Ein Projekt ist in einen anderen Lebensbereich gezogen (#220), aus seinen Aufgaben-Details oder
   * per Rückgängig. `MainLayout` zieht den Tab, der es zeigt, samt App in diesen Bereich.
   */
  'project-moved-life-space': { projectId: string; lifeSpace: LifeSpace }
  /**
   * Ein Sprung zu einer Aufgabe liegt bereit (`lib/taskJump`). Eine Seite, die schon offen ist,
   * holt ihn damit ab — ihr Daten-Effekt liefe sonst nicht noch einmal (#210).
   */
  'pending-task-jump': undefined
  /**
   * Ein Sprung auf die Pinnwand liegt bereit (`setPendingCanvasJump`, Treffer der **Suche**). Wie
   * `pending-task-jump`, für eine schon offene Projektseite.
   */
  'pending-canvas-jump': undefined
  /** Die Lupe in der Titelleiste wurde geklickt — `MainLayout` öffnet die **Suche** (#233). */
  'open-search': undefined
  /** Das „+“ in der Titelleiste wurde geklickt — `MainLayout` öffnet die **Schnelleingabe** (#256). */
  'open-quick-add': undefined
  /**
   * ⇧⌘F hat den Stern umgeschaltet (#210) — für die Aufgaben der Auswahl oder das Projekt. Ein
   * `FavoriteToggle`, der genau diese zeigt, bewegt sich dann wie beim Klick.
   */
  'favorite-shortcut': { ids: string[]; favorite: boolean }
  /**
   * „Anzeigen“ im Toast nach dem Anlegen an einem anderen Ort (#234) — `MainLayout` springt hin wie
   * zu einem Treffer der **Suche**.
   */
  'open-task': { task: Task }
  /**
   * Ein Punkt in **Entdecken** wurde geklickt (#323). Die Seitenleiste hat schon hingeführt; `DiscoverTip`
   * sucht den Anker (`data-intro-anchor`) und zeigt dort die Erklärung — sonst am Punkt selbst (`fallback`).
   */
  'intro-discover': { step: DiscoverStepId; fallback: HTMLElement | null }
  /** Hilfe › „Einführung neu starten“ (#323) — `MainLayout` legt an, was fehlt, und blendet „Entdecken“ ein. */
  'intro-restart': undefined
  /** Das ? über dem Profilbild (#323): Testdaten anlegen/entfernen wie über das App-Menü (#269), mit Rückfrage. */
  'testdata-request': { action: 'create' | 'remove' }
}

type EventPayload<K extends keyof AppEventMap> = AppEventMap[K]

/** Dispatch a typed custom event on `window`. */
export function dispatchAppEvent<K extends keyof AppEventMap>(
  type: K,
  ...args: EventPayload<K> extends undefined ? [] : [EventPayload<K>]
): void {
  window.dispatchEvent(new CustomEvent(type, { detail: args[0] }))
}

/** Subscribe to a typed custom event. Returns an unsubscribe function. */
export function onAppEvent<K extends keyof AppEventMap>(
  type: K,
  handler: EventPayload<K> extends undefined ? () => void : (payload: EventPayload<K>) => void,
): () => void {
  const listener = (e: Event) => {
    (handler as (payload: any) => void)((e as CustomEvent).detail)
  }
  window.addEventListener(type, listener)
  return () => window.removeEventListener(type, listener)
}
